import { PersonModel } from '@models/person.model';

export namespace PersonGroupsModel {
  export enum AddPersonToGroupDialogTab {
    SavedPersons = 'savedPersons',
    UnsavedPersons = 'unsavedPersons',
  }

  export interface PersonGroupDocument {
    _id: string;
    name: string;
    personIds: number[];
    createdByOrgId: string;
    accessList: PersonGroupAccessItem[];
  }

  export interface PersonGroupAccessItem {
    orgId: string;
    role: ShareAccessRole;
    grantedByUserId: string;
    grantedAt: number;
  }

  export interface PersonGroupListDocument extends Omit<PersonGroupDocument, 'personIds' | 'accessList'> {
    personsCounter: number;
    accessListCounter: number;
    activeOrgAccessRole: ShareAccessRole;
  }

  export interface PersonGroupDetailsDocument extends PersonGroupDocument {
    personsCounter: number;
    accessListCounter: number;
    activeOrgAccessRole: ShareAccessRole;
  }

  export enum RowActionId {
    Share = 'share',
    Edit = 'edit',
    Delete = 'delete',
  }

  export enum ConfirmAction {
    RemovePersonGroupShareOrg = 'remove_person_group_share_org',
  }

  export interface DatasetRowActionEvent {
    actionId: string;
    row: PersonGroupListDocument;
  }

  export interface GroupNameDialogData {
    initialName?: string;
    titleKey?: string;
    submitKey?: string;
  }

  export interface JoinGroupDialogData {
    initialCode?: string;
    titleKey?: string;
    submitKey?: string;
  }

  /** Query param keys when opening join group via `RouteModalService`. */
  export enum JoinRouteQueryParam {
    InviteCode = 'inviteCode',
  }

  export enum ShareAccessRole {
    Admin = 'admin',
    Contributor = 'contributor',
    Viewer = 'viewer',
  }

  export const SHARE_ACCESS_ROLES: ShareAccessRole[] = [
    ShareAccessRole.Admin,
    ShareAccessRole.Contributor,
    ShareAccessRole.Viewer,
  ];

  export interface PersonGroupSharedWithRow {
    orgId: string;
    orgName: string;
    accessType: ShareAccessRole;
  }

  export interface ShareGroupDialogData {
    groupId: string;
    groupName: string;
    createdByOrgId: string;
  }

  export interface AddPersonToGroupDialogData {
    groupId: string;
    groupName: string;
    personGroupMemberIds: number[];
  }

  export interface AddPersonToGroupsDialogData {
    personType: AddPersonToGroupDialogTab;
    personIds: number[];
    unsavedPersonDtos: PersonModel.UnsavedPersonDto[];
  }

  /** Payload applied to store after a successful share-options load. */
  export interface PersonGroupShareOptionsPayload {
    groupId: string;
    inviteCode: string;
    accessList: PersonGroupSharedWithRow[];
  }

  export interface RequestRemovePersonsFromGroupPayload {
    groupId: string;
    groupName: string;
    personIds: number[];
    requestId: string;
  }

  export interface RemovePersonsFromGroupPayload {
    groupId: string;
    personIds: number[];
    requestId: string;
  }

  export interface RemovePersonsFromGroupSuccessPayload {
    groupId: string;
    removedPersonIds: number[];
    requestId: string;
  }

  export interface RemovePersonsFromGroupFailPayload {
    requestId: string;
    personIds: number[];
  }

  export interface RemovePersonsFromGroupCancelPayload {
    requestId: string;
  }

  export interface AddPersonsToGroupsActionPayload {
    groupIds: string[];
    savedPersonIds: number[];
    unsavedPersonDtos: PersonModel.UnsavedPersonDto[];
  }

  export interface AddPersonsToGroupsPreparedPayload {
    groupIds: string[];
    savedPersonIds: number[];
    createdPendingPersons: PersonModel.SavedPerson[];
  }

  export interface AddPersonsToGroupsAPIPayload {
    groupIds: string[];
    savedPersonIds: number[];
  }

  export interface AddPersonsToGroupsSuccessPayload {
    groupIds: string[];
    addedPersons: PersonModel.SavedPerson[];
  }

  export interface GroupSearchItem {
    groupId: string;
    name: string;
  }

  export function isShareAccessRole(value: string): value is ShareAccessRole {
    return (
      value === ShareAccessRole.Admin ||
      value === ShareAccessRole.Contributor ||
      value === ShareAccessRole.Viewer
    );
  }

  export function shareAccessTypeTranslationKey(role: ShareAccessRole): string {
    switch (role) {
      case ShareAccessRole.Admin:
        return 'PERSON_GROUPS.SHARE_DIALOG.ROLES.ADMIN';
      case ShareAccessRole.Contributor:
        return 'PERSON_GROUPS.SHARE_DIALOG.ROLES.CONTRIBUTOR';
      case ShareAccessRole.Viewer:
        return 'PERSON_GROUPS.SHARE_DIALOG.ROLES.VIEWER';
    }
  }

  /**
   * Resolves the active org's role for share UI using the access list plus creator org (creator may be absent from rows).
   * Align with backend `PersonGroupsModel.resolveActiveOrgAccessRole` / `resolveRole`.
   */
  export function resolveMyShareAccessRoleFromSharedWithRows(
    accessList: readonly PersonGroupSharedWithRow[],
    createdByOrgId: string,
    activeOrgId: string | null,
  ): ShareAccessRole | null {
    if (activeOrgId == null || activeOrgId === '') {
      return null;
    }
    if (activeOrgId === createdByOrgId) {
      return ShareAccessRole.Admin;
    }
    return accessList.find((row) => row.orgId === activeOrgId)?.accessType ?? null;
  }

  /**
   * Whether `inviteRole` may be assigned via invite when the caller has `callerRole`.
   * Keep in sync with backend `PersonGroupsModel.isInviteShareRoleAllowedForCaller`.
   */
  export function isInviteShareRoleAllowedForCaller(
    callerRole: ShareAccessRole,
    inviteRole: ShareAccessRole,
  ): boolean {
    switch (callerRole) {
      case ShareAccessRole.Admin:
        return true;
      case ShareAccessRole.Contributor:
        return inviteRole === ShareAccessRole.Contributor || inviteRole === ShareAccessRole.Viewer;
      case ShareAccessRole.Viewer:
        return inviteRole === ShareAccessRole.Viewer;
      default:
        return false;
    }
  }

  /**
   * Invite roles the caller may issue, highest privilege first (default selection).
   */
  export function invitableShareRolesForCaller(callerRole: ShareAccessRole | null): ShareAccessRole[] {
    if (callerRole == null) {
      return [];
    }
    return SHARE_ACCESS_ROLES.filter((inviteRole) =>
      PersonGroupsModel.isInviteShareRoleAllowedForCaller(callerRole, inviteRole),
    );
  }

  export function isDuplicatePersonGroupName(
    name: string,
    groups: PersonGroupListDocument[],
    excludeId?: string,
  ): boolean {
    const normalized = name.trim().toLowerCase();
    return groups.some(
      (g) => g._id !== excludeId && g.name.trim().toLowerCase() === normalized,
    );
  }

  export namespace API {
    export interface PersonGroupListItem {
      _id: string;
      name: string;
      personsCounter: number;
      accessListCounter: number;
      createdByOrgId: string;
      activeOrgAccessRole: ShareAccessRole;
    }

    export interface PersonGroupDetails {
      _id: string;
      name: string;
      personIds: number[];
      createdByOrgId: string;
      accessList: PersonGroupAccessItem[];
      personsCounter: number;
      accessListCounter: number;
      activeOrgAccessRole: ShareAccessRole;
      createdAt: number;
      updatedAt: number;
    }

    export interface GroupPersonsResponse {
      groupId: string;
      persons: PersonModel.SavedPerson[];
    }

    export interface AccessListResponse {
      groupId: string;
      createdByOrgId: string;
      accessList: PersonGroupSharedWithRow[];
    }

    export interface InviteCodeResponse {
      groupId: string;
      role: ShareAccessRole;
      inviteCode: string;
    }

    export interface AddPersonsToGroupsResponse {
      groupIds: string[];
      affectedPersonIds: number[];
      savedPersons: PersonModel.SavedPerson[];
    }
  }
}
